import { watch, onScopeDispose, toValue, type MaybeRefOrGetter, type Ref } from 'vue';

export type UseOnOutsideEventOptions = {
    /** Target the listener is attached to, e.g. `document` or `window`. */
    eventTarget: EventTarget;
    /** Name of the DOM event to listen to. */
    eventName: string;
    /** Called when the event originates outside every element in `excludeEls`. */
    callback: () => void;
    /**
     * Refs to elements that are considered "inside" areas.
     * Empty by default — nothing is excluded, so every event counts as outside.
     */
    excludeEls?: Ref<HTMLElement | null>[];
    /** Reactive flag that drives listener lifecycle. Always active by default. */
    isActive?: MaybeRefOrGetter<boolean>;
    /** Registers the listener as passive. `false` by default. */
    passive?: boolean;
};

/**
 * Base primitive behind the "dismiss on an outside event" hooks.
 *
 * Attaches `eventName` on `eventTarget` in the capture phase and invokes
 * `callback` when the event originates outside every element in `excludeEls`.
 * Capture is mandatory: the listener has to run before anything in the subtree
 * can stop propagation.
 *
 * `callback` fires unconditionally when there is nothing to test against —
 * an empty `excludeEls`, or an event whose target is not a DOM node.
 *
 * The listener is attached immediately if `isActive` already resolves to
 * `true`, follows its further changes, and is automatically removed on
 * scope disposal.
 *
 * Prefer the dedicated wrappers — {@link useClickOutside},
 * {@link useOnScrollDismiss} — and reach for this one only for an event
 * they do not cover.
 */
export const useOnOutsideEvent = ({
    eventTarget,
    eventName,
    callback,
    excludeEls = [],
    isActive = true,
    passive = false,
}: UseOnOutsideEventOptions): void => {
    const onOutsideEvent = (event: Event) => {
        const { target } = event;

        // Nothing to exclude, or no DOM node to test against: either way the event
        // cannot have originated inside an excluded element.
        if (!excludeEls.length || !(target instanceof Node)) {
            callback();

            return;
        }

        const checkIsInsideExcluded = (excludeEl: Ref<HTMLElement | null>) => {
            return excludeEl.value?.contains(target) ?? false;
        };

        if (excludeEls.some(checkIsInsideExcluded)) return;

        callback();
    };

    const attachListeners = () => {
        eventTarget.addEventListener(eventName, onOutsideEvent, { capture: true, passive });
    };

    const detachListeners = () => {
        eventTarget.removeEventListener(eventName, onOutsideEvent, { capture: true });
    };

    // immediate: true — `isActive` may already be `true` (or the constant default) at call time.
    // flush: 'sync' — runs immediately on source mutation, before any async ticks.
    watch(
        () => toValue(isActive),
        (active) => active ? attachListeners() : detachListeners(),
        { immediate: true, flush: 'sync' }
    );

    onScopeDispose(detachListeners);
};
